import React, { useState, useEffect, useMemo } from "react";
import { Button, DataTable, ActionButton, TableCellStack, TableCellInline, Tag } from "./ui";
import { DEFAULT_TABLE_PAGE_SIZE } from "./ui/tableUtils";
import { useNavigate } from "react-router-dom";
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBModal, MDBModalHeader, MDBModalBody, MDBModalFooter, MDBIcon, MDBSpinner, MDBTooltip, MDBModalDialog, MDBModalContent } from "mdb-react-ui-kit";
import { api } from "../services";
import Toolbar from "./Toolbar";
import Actionbar from "./Actionbar";
import { useTranslation } from "react-i18next";
import { CardComponent } from "./CardComponent";
import { Check, Copy } from "lucide-react";
import { usePermission } from "../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../config/routePermissions";
import {
  resolveDateLocale,
  formatLocalizedDate,
  formatLocalizedTime,
} from "../utils/formatDateTime";

function StoreBranchOverview() {
  const {t,i18n} = useTranslation("translation")
  const isArabic = i18n.language === "ar";
  const dateLocale = resolveDateLocale(i18n.language);
  const { can } = usePermission();
  const canCreateStore = can(RBAC_MODULES.SETTINGS, PERMISSION_ACTIONS.ADD);
  const canEditStore = can(RBAC_MODULES.SETTINGS, PERMISSION_ACTIONS.EDIT);
  const canDeleteStore = can(RBAC_MODULES.SETTINGS, PERMISSION_ACTIONS.DELETE);
  /** Same capability as backend `settings:view` — list branches without requiring settings:update. */
  const canViewBranchesNav = can(RBAC_MODULES.SETTINGS, PERMISSION_ACTIONS.VIEW);
  const [viewMode, setViewMode] = useState("list");
  const [storesData, setStoresData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [storeToDelete, setStoreToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [tableSortColumn, setTableSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserStores();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    
    setTimeout(() => {
      setCopiedId(null);
    }, 1000);
  };

  const fetchUserStores = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");
      const response = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStoresData(response.data.stores || []);
    } catch (error) {
      console.error(
        "Error fetching stores:",
        error.response?.data || error.message
      );
      setError(t("Failed to fetch stores. Please try again later."));
    } finally {
      setLoading(false);
    }
  };

  const handleViewModeChange = (mode) => setViewMode(mode);
  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const handleEditClick = (storeId) => {
    if (!canEditStore) {
      setError(t("noPermissionToAccessThis"));
      return;
    }
    navigate(`/edit-store?storeId=${encodeURIComponent(storeId)}`, {
      state: { storeId },
    });
  };

  const handleManageBranchesClick = (storeId, storeName) => {
    if (!canViewBranchesNav) {
      setError(t("noPermissionToAccessThis"));
      return;
    }
    navigate("/manage-branches-v2", { state: { storeId, storeName } });
  };

  const handleAddStoreClick = () => {
    if (!canCreateStore) {
      setError(t("noPermissionToAccessThis"));
      return;
    }
    navigate("/add-store");
  };

  const handleDeleteClick = (store) => {
    if (!canDeleteStore) {
      setError(t("noPermissionToAccessThis"));
      return;
    }
    setStoreToDelete(store);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!storeToDelete) return;

    // Additional permission validation
    if (!canDeleteStore) {
      setError(t("noPermissionToAccessThis"));
      setDeleteModal(false);
      setStoreToDelete(null);
      return;
    }

    try {
      setIsDeleting(true);
      const token = localStorage.getItem("token");
      await api.delete(`/stores/${storeToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Refresh the store list
      await fetchUserStores();
      setDeleteModal(false);
      setStoreToDelete(null);
    } catch (error) {
      console.error("Error deleting store:", error);

      // Handle 400 Bad Request with specific error message
      if (error.response?.status === 400) {
        // Use server-provided message if available, otherwise fallback
        const serverMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          "The store cannot be deleted";

        // More specific message based on your API's response structure
        setError(t("Failed to delete store: {{message}}", { message: serverMessage }));
      }
      // Handle other error statuses
      else if (error.response?.status === 401) {
        setError(t("Unauthorized: Please login again"));
      } else if (error.response?.status === 404) {
        setError(t("Store not found - it may have already been deleted"));
      } else {
        // Generic error for other cases
        setError(t("Failed to delete store. Please try again later."));
      }

      setDeleteModal(false);
      setStoreToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredStores = useMemo(
    () =>
      storesData.filter((store) =>
        store.name.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [storesData, searchQuery]
  );

  const sortedStores = useMemo(() => {
    const list = [...filteredStores];
    const dir = sortDirection === "asc" ? 1 : -1;

    const getSortValue = (store, column) => {
      switch (column) {
        case "name":
          return (store.name || "").toLowerCase();
        case "storeId":
          return store._id || "";
        case "branches":
          return store.branches?.length ?? 0;
        case "createdAt":
          return store.createdAt ? new Date(store.createdAt).getTime() : 0;
        default:
          return "";
      }
    };

    list.sort((a, b) => {
      const av = getSortValue(a, tableSortColumn);
      const bv = getSortValue(b, tableSortColumn);
      if (typeof av === "string" && typeof bv === "string") {
        return dir * av.localeCompare(bv);
      }
      return dir * (av < bv ? -1 : av > bv ? 1 : 0);
    });

    return list;
  }, [filteredStores, tableSortColumn, sortDirection]);

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedStores.slice(start, start + itemsPerPage);
  }, [sortedStores, currentPage, itemsPerPage]);

  const applyTableSort = (column, direction) => {
    setTableSortColumn(column);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const storeColumns = useMemo(
    () => [
      {
        id: "name",
        header: t("storeAndBranchesPage.storeTable.storeName"),
        strong: true,
        sortable: true,
        width: 200,
        minWidth: 168,
        ellipsis: true,
        render: (store) => (
          <TableCellStack
            primary={store.name || "-"}
            secondary={store.email || "-"}
            primaryTitle={store.name}
            secondaryTitle={store.email}
          />
        ),
      },
      {
        id: "storeId",
        header: t("Store ID"),
        sortable: true,
        width: 148,
        minWidth: 132,
        ellipsis: true,
        render: (store) => (
          <TableCellInline
            text={store._id}
            title={store._id}
            trailing={
              copiedId === store._id ? (
                <Check style={{ color: "green" }} size={15} aria-hidden="true" />
              ) : (
                <Copy
                  onClick={() => handleCopy(store._id, store._id)}
                  style={{ cursor: "pointer" }}
                  size={15}
                  aria-label="Copy"
                />
              )
            }
          />
        ),
      },
      {
        id: "branches",
        header: t("storeAndBranchesPage.storeTable.branches"),
        sortable: true,
        width: 100,
        minWidth: 88,
        render: (store) => (
          <Tag variant="info">{store.branches.length}</Tag>
        ),
      },
      {
        id: "createdAt",
        header: t("createdAt"),
        width: 156,
        minWidth: 140,
        sortable: true,
        render: (store) =>
          store.createdAt ? (
            <div className="oh-table-date-cell" dir="ltr">
              <span className="oh-table-date-cell__date">
                {formatLocalizedDate(store.createdAt, dateLocale)}
              </span>
              <span className="oh-table-date-cell__time">
                {formatLocalizedTime(store.createdAt, dateLocale)}
              </span>
            </div>
          ) : (
            "-"
          ),
      },
      {
        id: "actions",
        header: t("storeAndBranchesPage.storeTable.actions"),
        width: 280,
        minWidth: 280,
        className: "oh-table-td--actions",
        headerClassName: "oh-table-th--actions",
        render: (store) => (
          <div
            className="d-flex gap-2 align-items-center flex-wrap oh-table-actions"
            onClick={(e) => e.stopPropagation()}
          >
            {canViewBranchesNav ? (
              <ActionButton
                variant="branches"
                showIcon={false}
                onClick={() => handleManageBranchesClick(store._id, store.name)}
              >
                {t("branches")}
              </ActionButton>
            ) : (
              <MDBTooltip tag="span" title={t("noPermissionToAccessThis")}>
                <span className="d-inline-flex">
                  <ActionButton variant="branches" showIcon={false} disabled>
                    {t("branches")}
                  </ActionButton>
                </span>
              </MDBTooltip>
            )}
            {canEditStore ? (
              <ActionButton
                variant="edit"
                onClick={() => handleEditClick(store._id)}
              >
                {t("edit")}
              </ActionButton>
            ) : (
              <MDBTooltip tag="span" title={t("noPermissionToAccessThis")}>
                <span className="d-inline-flex">
                  <ActionButton variant="edit" disabled>
                    {t("edit")}
                  </ActionButton>
                </span>
              </MDBTooltip>
            )}
            {canDeleteStore ? (
              <ActionButton
                variant="delete"
                onClick={() => handleDeleteClick(store)}
              >
                {t("delete")}
              </ActionButton>
            ) : (
              <MDBTooltip tag="span" title={t("noPermissionToAccessThis")}>
                <span className="d-inline-flex">
                  <ActionButton variant="delete" disabled>
                    {t("delete")}
                  </ActionButton>
                </span>
              </MDBTooltip>
            )}
          </div>
        ),
      },
    ],
    [
      t,
      dateLocale,
      copiedId,
      canViewBranchesNav,
      canEditStore,
      canDeleteStore,
      handleCopy,
      handleManageBranchesClick,
      handleEditClick,
      handleDeleteClick,
    ]
  );

  return (
    <div className="store-branches-page-root oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <h1 className="page-title-header">{t("storeAndBranchesPage.pageTitle")}</h1>

        {/* Error Message */}
        {error && (
          <div className="alert alert-danger mb-4">
            <MDBIcon icon="exclamation-circle" className="me-2" />
            {error}
          </div>
        )}

        {/* Statistics Section */}
        <div className="oh-page-section">
          <div className="oh-kpi-row oh-kpi-row--compact">
            <CardComponent
              title={t("storeAndBranchesPage.totalStores")}
              value={storesData.length}
              loading={loading}
            />
            <CardComponent
              title={t("storeAndBranchesPage.totalBranches")}
              value={storesData.reduce(
                (acc, store) => acc + store.branches.length,
                0
              )}
              loading={loading}
            />
          </div>
        </div>
          {/* <MDBCol md="4">
            <MDBCard className="stat-card h-100 shadow-sm">
              <MDBCardBody className="py-4">
                <h6 className="text-muted mb-3">{t("storeAndBranchesPage.activeStores")}</h6>
                <h3 className="mb-0 fw-bold">
                  {loading ? (
                    <MDBSpinner size="sm" />
                  ) : (
                    storesData.filter((store) => store.status === "Active")
                      .length
                  )}
                </h3>
              </MDBCardBody>
            </MDBCard>
          </MDBCol> */}

        {/* Quick Actions */}
        <div className="oh-page-section">
        <Actionbar
  title={t("storeAndBranchesPage.storeAndBranchesActionbar.title")}
  subtitle={t("storeAndBranchesPage.storeAndBranchesActionbar.subTitle")}
  secondBtnText={t("storeAndBranchesPage.storeAndBranchesActionbar.refreshBtn")}
  secondBtnOnClick={fetchUserStores}
  secondBtnClass="secondary"
  secondBtnIcon="refresh"
/>
        </div>

        {/* Search and View Toggle */}
       
              {/* <MDBCol md="6">
                <MDBInput
                  type="text"
                  label="Search Stores"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="mb-3 mb-md-0"
                />
              </MDBCol> */}
              {/* <MDBCol
                md="6"
                className="d-flex justify-content-md-end align-items-center"
              >
                <div className="d-flex align-items-center me-3">
                  <span className="me-2 text-muted">Sort by:</span>
                  <MDBDropdown>
                    <MDBDropdownToggle color="light" className="shadow-none">
                      Recent
                    </MDBDropdownToggle>
                    <MDBDropdownMenu>
                      <MDBDropdownItem link>Recent</MDBDropdownItem>
                      <MDBDropdownItem link>Oldest</MDBDropdownItem>
                      <MDBDropdownItem link>Name (A-Z)</MDBDropdownItem>
                      <MDBDropdownItem link>Name (Z-A)</MDBDropdownItem>
                    </MDBDropdownMenu>
                  </MDBDropdown>
                </div>
                <div className="btn-group shadow-none">
                  <Button
                    color={viewMode === "list" ? "primary" : "light"}
                    onClick={() => handleViewModeChange("list")}
                    outline={viewMode !== "list"}
                  >
                    <MDBIcon icon="list" />
                  </Button>
                  <Button
                    color={viewMode === "grid" ? "primary" : "light"}
                    onClick={() => handleViewModeChange("grid")}
                    outline={viewMode !== "grid"}
                  >
                    <MDBIcon icon="th-large" />
                  </Button>
                </div>
              </MDBCol> */}

               <div className="oh-page-section">
                 <Toolbar 
                showSearch={true} 
                searchQuery={searchQuery} 
                handleSearch={handleSearchChange} 
                searchPlaceHolder={t("Search store")}
                showViewMode={true}
                viewMode={viewMode}
                handleViewChange={setViewMode}
                />
               </div>

             
        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <MDBSpinner size="lg" color="primary" className="me-2" />
            <span>Loading stores...</span>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredStores.length === 0 && (
          <MDBCard className="text-center py-5">
            <MDBCardBody>
              <MDBIcon icon="store" size="4x" className="text-muted mb-4" />
              <h4 className="mb-3">No Stores Found</h4>
              <p className="text-muted mb-4">
                {searchQuery
                  ? "No stores match your search."
                  : "You have not added any stores yet."}
              </p>
              {canCreateStore && (
              <Button color="primary" onClick={handleAddStoreClick}>
                Add Your First Store
              </Button>
              )}
            </MDBCardBody>
          </MDBCard>
        )}

        {/* Store Content */}
        {!loading && filteredStores.length > 0 && (
          <>
            {viewMode === "grid" ? (
              <MDBRow className="g-4">
                {sortedStores.map((store) => (
                  <MDBCol md="4" xl="3" key={store._id}>
                    <MDBCard 
                      className="h-100 border-0"
                      style={{
                        borderRadius: "16px",
                        boxShadow: "0 2px 16px #F0F4F7",
                        overflow: "hidden",
                        transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                        border: "1px solid rgba(0, 0, 0, 0.06)",
                        backgroundColor: "#fefefe",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-4px)";
                        e.currentTarget.style.boxShadow = "0 8px 32px #F0F4F7";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 2px 16px #F0F4F7";
                      }}
                    >
                      <MDBCardBody className="d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <h5 className="mb-0 fw-bold">{store.name}</h5>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted d-block">
                            {store.email}
                          </small>
                          <small className="text-muted d-block">
                            {t("integrationid")} : {store._id}
                          </small>
                        </div>
                        <div className="text-center my-3 py-2 bg-light rounded">
                          <h6 className="mb-0 fw-bold">
                            {store.branches.length}{" "}
                            {store.branches.length === 1
                              ? "Branch"
                              : "Branches"}
                          </h6>
                        </div>

                        {/* Footer */}
                        <div
                          className="mt-auto pt-3 d-flex justify-content-end gap-2 flex-nowrap align-items-center"
                          style={{
                            borderTop: "1px solid rgba(0, 0, 0, 0.05)",
                            marginLeft: "-24px",
                            marginRight: "-24px",
                            marginBottom: "-24px",
                            paddingLeft: "24px",
                            paddingRight: "24px",
                            paddingTop: "12px",
                            paddingBottom: "12px",
                          }}
                        >
                          {canViewBranchesNav ? (
                            <ActionButton
                              variant="branches"
                              showIcon={false}
                              onClick={() =>
                                handleManageBranchesClick(store._id, store.name)
                              }
                            >
                              {t("branches")}
                            </ActionButton>
                          ) : (
                            <MDBTooltip tag="span" title={t("noPermissionToAccessThis")}>
                              <span className="d-inline-flex">
                                <ActionButton variant="branches" showIcon={false} disabled>
                                  {t("branches")}
                                </ActionButton>
                              </span>
                            </MDBTooltip>
                          )}
                          {canEditStore ? (
                            <ActionButton
                              variant="edit"
                              onClick={() => handleEditClick(store._id)}
                            >
                              {t("edit")}
                            </ActionButton>
                          ) : (
                            <MDBTooltip tag="span" title={t("noPermissionToAccessThis")}>
                              <span className="d-inline-flex">
                                <ActionButton variant="edit" disabled>
                                  {t("edit")}
                                </ActionButton>
                              </span>
                            </MDBTooltip>
                          )}
                          {canDeleteStore ? (
                            <ActionButton
                              variant="delete"
                              onClick={() => handleDeleteClick(store)}
                            >
                              {t("delete")}
                            </ActionButton>
                          ) : (
                            <MDBTooltip tag="span" title={t("noPermissionToAccessThis")}>
                              <span className="d-inline-flex">
                                <ActionButton variant="delete" disabled>
                                  {t("delete")}
                                </ActionButton>
                              </span>
                            </MDBTooltip>
                          )}
                        </div>
                      </MDBCardBody>
                    </MDBCard>
                  </MDBCol>
                ))}
              </MDBRow>
            ) : (
              <DataTable
                tableId="store-branch-overview"
                title={t("sidebar.storesAndBranches")}
                className="store-branch-overview-table"
                tableMinWidth={920}
                maxBodyHeight={700}
                columns={storeColumns}
                data={currentItems}
                rowKey="_id"
                selectionMode="index"
                isRtl={isArabic}
                primaryAction={
                  canCreateStore
                    ? {
                        label: t("storeAndBranchesPage.storeAndBranchesActionbar.addStoreBtn"),
                        onClick: handleAddStoreClick,
                      }
                    : {
                        label: t("storeAndBranchesPage.storeAndBranchesActionbar.addStoreBtn"),
                        onClick: handleAddStoreClick,
                        disabled: true,
                        title: t("noPermissionToAccessThis"),
                      }
                }
                sort={{
                  column: tableSortColumn,
                  direction: sortDirection,
                  onChange: ({ column, direction }) =>
                    applyTableSort(column, direction),
                }}
                pagination={{
                  page: currentPage,
                  pageSize: itemsPerPage,
                  total: sortedStores.length,
                  onPageChange: setCurrentPage,
                  onPageSizeChange: (size) => {
                    setItemsPerPage(size);
                    setCurrentPage(1);
                  },
                  pageSizeOptions: [10, 20, 25, 50],
                  showLabel: t("Show", { defaultValue: "Show" }),
                  ofLabel: t("of", { defaultValue: "Of" }),
                  resultsLabel: t("results", { defaultValue: "results" }),
                  isRtl: isArabic,
                  previousLabel: t("previous"),
                  nextLabel: t("next"),
                }}
              />
            )}
          </>
        )}

        {/* Delete Confirmation Modal */}
        <MDBModal open={deleteModal} setShow={setDeleteModal} tabIndex="-1">
          <MDBModalDialog scrollable>
            <MDBModalContent>
              <MDBModalHeader className="d-flex align-items-center" style={{ justifyContent: 'flex-start' }}>
                {isArabic ? (
                  <>
                    <span style={{ order: 2 }}>{t("storeAndBranchesPage.deleteModal.title")}</span>
                    <MDBIcon
                      icon="exclamation-triangle"
                      color="danger"
                      className="ms-2"
                      style={{ order: 1 }}
                    />
                  </>
                ) : (
                  <>
                    <MDBIcon
                      icon="exclamation-triangle"
                      color="danger"
                      className="me-2"
                    />
                    <span>{t("storeAndBranchesPage.deleteModal.title")}</span>
                  </>
                )}
              </MDBModalHeader>
              <MDBModalBody>
               {t("storeAndBranchesPage.deleteModal.confirmText")}{" "}
                <strong>{storeToDelete?.name}.</strong> {t("storeAndBranchesPage.deleteModal.warningText")}
              </MDBModalBody>
              <MDBModalFooter>
                <Button
                  color="secondary"
                  onClick={() => setDeleteModal(false)}
                  disabled={isDeleting}
                >
                  {t("cancel")}
                </Button>
                <Button
                  color="danger"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <MDBSpinner size="sm" className={isArabic ? "ms-2" : "me-2"} />
                      {t("deleting")}...
                    </>
                  ) : (
                   t("delete")
                  )}
                </Button>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

      </MDBContainer>
    </div>
  );
}

export default StoreBranchOverview;
